import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Compass, Anchor, ArrowDown, GraduationCap, Users, Check, X, Video, Sparkles, Quote, ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import api from '../lib/api';
import { scrollToSection } from '../hooks/useSmoothScroll';

const Landing = () => {
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error('Por favor, acepta el consentimiento para continuar');
      return;
    }
    if (!email) {
      toast.error('Por favor, introduce tu email');
      return;
    }

    setSubscribing(true);
    try {
      const response = await api.post('/emails/subscribe', { email, consent });
      if (response.data.already_subscribed) {
        toast.info('Ya estás suscrito a nuestra lista');
      } else {
        toast.success('¡Gracias! Revisa tu email para descargar el ebook');
        setEmail('');
        setConsent(false);
      }
    } catch (error) {
      toast.error('Ha ocurrido un error. Inténtalo de nuevo.');
    } finally {
      setSubscribing(false);
    }
  };

  const testimonials = [
    { name: "Isabel", role: "Mentoría individual", text: "Me llevo una conexión interna real. Ahora soy consciente de lo desconectada que estaba. Es un lugar de autonomía, de energía y de poder personal.", highlight: "Conexión interna real" },
    { name: "María", role: "Taller Hieros Gamos", text: "Por primera vez me hice consciente de que sentía amor, pero no hacia mí misma. Ahora llevo días sintiéndome como el sofá al lado de la chimenea… para mí.", highlight: "Autoacogida profunda" },
    { name: "Vanessa", role: "Taller Hieros Gamos", text: "La sensación ha sido la de haber dado un salto hacia adelante. Dejar de darle vueltas a la cabeza y volver al sentir.", highlight: "Menos mente, más presencia" },
    { name: "Elisenda", role: "Taller Hieros Gamos", text: "Mi mente, que siempre va al 200%, se ha parado por completo durante días. No observaba, no analizaba, simplemente estaba.", highlight: "Parada mental profunda" },
    { name: "Patricia", role: "Taller Hieros Gamos", text: "Todo lo que se trabajaba pasaba por el corazón, no por la mente. Cuando algo lo puedes explicar desde ahí, es que ya está integrado.", highlight: "Comprensión integradora" },
    { name: "Ana", role: "Taller Hieros Gamos", text: "He sentido muy claro tres lugares del planeta, con una conexión muy viva. Ha sido una sensación muy real de integración y cierre.", highlight: "Recuperación de energía" }
  ];

  // Auto-play for testimonials carousel
  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      setTestimonialIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, testimonials.length]);

  const goToTestimonial = (index) => {
    setTestimonialIndex(index);
    setIsAutoPlaying(false);
  };

  const nextTestimonial = () => {
    setTestimonialIndex((prev) => (prev + 1) % testimonials.length);
    setIsAutoPlaying(false);
  };

  const prevTestimonial = () => {
    setTestimonialIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    setIsAutoPlaying(false);
  };

  return (
    <div className="bg-[#faf8f5] pb-20">
      {/* ==================== HERO - Anillos concéntricos ==================== */}
      <section className="relative min-h-[90vh] flex items-center justify-center pt-20 overflow-hidden">
        {/* Background rings */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-[700px] h-[700px] rounded-full border border-[#c9a227]/10 absolute" />
          <div className="w-[550px] h-[550px] rounded-full border border-[#c9a227]/15 absolute" />
          <div className="w-[400px] h-[400px] rounded-full border border-[#c9a227]/20 absolute" />
          <div className="w-[250px] h-[250px] rounded-full border border-[#c9a227]/25 absolute" />
          <div className="w-[120px] h-[120px] rounded-full bg-gradient-to-br from-[#c9a227]/20 to-transparent absolute" />
        </div>
        
        {/* Content */}
        <div className="relative text-center max-w-3xl px-6 py-16 lg:py-24">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 text-[#c9a227] rounded-full text-sm font-medium mb-10 shadow-sm">
            <Sparkles className="w-4 h-4" />
            Escuela de Consciencia Akáshica
          </div>
          
          {/* Main title */}
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-heading text-[hsl(var(--text-primary))] tracking-tight mb-6 leading-[1.1]">
            Un espacio para
            <span className="block text-[#c9a227]">volver a ti</span>
          </h1>
          
          {/* Subtitle */}
          <p className="text-xl sm:text-2xl text-[hsl(var(--text-secondary))] max-w-2xl mx-auto mb-10 leading-relaxed font-light">
            Sin prisa. Sin ruido. De la desconexión y el esfuerzo constante, 
            a habitar tu cuerpo y tu vida con claridad.
          </p>
          
          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => scrollToSection('ebook')} className="bg-[#c9a227] hover:bg-[#b89220] text-white px-8 py-6 text-base rounded-full shadow-lg">
                Descarga el ebook gratuito
                <ArrowDown className="ml-2 w-4 h-4" />
              </Button>
            <Link to="/formaciones">
              <Button variant="outline" className="border-[#c9a227] text-[#c9a227] hover:bg-[#c9a227]/10 bg-white/50 px-8 py-6 text-base rounded-full">
                Ver formaciones
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
          
          {/* Scroll indicator */}
          <div className="mt-16 animate-bounce">
            <ArrowDown className="w-5 h-5 mx-auto text-[hsl(var(--text-tertiary))]" />
          </div>
        </div>
      </section>

      {/* ==================== RETIRO REINICIA — Banner promocional ==================== */}
      <section className="py-12 lg:py-16 bg-[#FDFBF5]" data-testid="home-retreat-banner-section">
        <div className="max-w-5xl mx-auto px-6">
          <Link
            to="/retiros/reinicia-2026"
            className="group relative block rounded-3xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500 ring-1 ring-[#D4A84B]/20 hover:ring-[#D4A84B]/50"
            data-testid="home-retreat-banner-link"
          >
            <img
              src="https://res.cloudinary.com/dpppkdhdk/image/upload/v1779991720/ChatGPT_Image_28_may_2026_20_08_11_nxjntp.png"
              alt="REINICIA 2026 · Retiro presencial · Monasterio del Olivar · 19-22 julio 2026"
              loading="lazy"
              className="block w-full h-auto group-hover:scale-[1.01] transition-transform duration-700"
            />
            {/* Subtle CTA overlay bottom-right */}
            <div className="absolute bottom-3 right-3 sm:bottom-5 sm:right-5 inline-flex items-center gap-1.5 bg-white/90 backdrop-blur-sm text-[#3a2e10] px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium shadow-md group-hover:bg-white transition-colors">
              <Sparkles className="w-3.5 h-3.5 text-[#D4A84B]" />
              Descubre el retiro
              <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
            </div>
          </Link>
        </div>
      </section>

      {/* ==================== EL PROBLEMA ==================== */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-xl lg:text-2xl text-[hsl(var(--text-secondary))] leading-relaxed font-light italic">
            "Quizás sientes que vives hacia afuera. 
            <span className="block mt-4">Que sostienes mucho, pero nadie te sostiene a ti.</span>
            <span className="block mt-4">Que algo dentro pide bajar el ritmo, pero no sabes cómo."</span>
          </p>
          
          <div className="mt-12 w-16 h-[1px] bg-[#c9a227] mx-auto" />
          
          <p className="mt-12 text-lg text-[hsl(var(--text-primary))] font-medium">
            Este espacio existe para eso: para aprender a volver.
          </p>
        </div>
      </section>

      {/* ==================== 3 PILARES ==================== */}
      <section className="py-20 lg:py-28 bg-[#faf8f5]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-heading text-[hsl(var(--text-primary))] mb-4">
              Tres pilares que sostienen todo
            </h2>
            <div className="w-16 h-[2px] bg-[#c9a227] mx-auto" />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Heart, title: 'Presencia', desc: 'Reconectar con tu cuerpo, sus sensaciones y su sabiduría. Dejar de vivir solo desde la mente.' },
              { icon: Compass, title: 'Claridad', desc: 'Distinguir el ruido de la verdad. Tomar decisiones desde un lugar centrado y honesto.' },
              { icon: Anchor, title: 'Sostén', desc: 'Desarrollar la capacidad de sostenerte a ti mismo y, si lo deseas, acompañar a otros.' }
            ].map((pillar, i) => (
              <div key={i} className="text-center p-8 bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow">
                <div className="w-16 h-16 rounded-full bg-[#c9a227]/10 flex items-center justify-center mx-auto mb-6">
                  <pillar.icon className="w-7 h-7 text-[#c9a227]" strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-heading text-[hsl(var(--text-primary))] mb-3">{pillar.title}</h3>
                <p className="text-[hsl(var(--text-secondary))] leading-relaxed">{pillar.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== FORMACIONES DESTACADAS ==================== */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-heading text-[hsl(var(--text-primary))] mb-4">
              Formas de profundizar
            </h2>
            <p className="text-lg text-[hsl(var(--text-secondary))]">Elige el camino que resuene con tu momento</p>
            <div className="w-16 h-[2px] bg-[#c9a227] mx-auto mt-6" />
          </div>
          
          <div className="space-y-6">
            {/* FACILITADORAS */}
            <Link to="/formaciones/especiales/facilitadoras-sosten-energetico" className="block group">
              <div className="relative rounded-2xl overflow-hidden bg-[#8a5a7a] p-8 lg:p-10 hover:shadow-xl transition-all">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                  <div className="space-y-4">
                    <div className="flex flex-wrap gap-2">
                      <span className="px-3 py-1 bg-white/20 text-white rounded-full text-sm">Formación profesional</span>
                      <span className="px-3 py-1 bg-[#c9a227] text-white rounded-full text-sm">3ª Edición</span>
                    </div>
                    <h3 className="text-2xl lg:text-3xl font-heading text-white">
                      Facilitadoras en Sostén Energético
                    </h3>
                    <p className="text-white/80 max-w-xl">
                      Para quienes sienten el llamado de acompañar a otros en sus procesos de transformación. 12 módulos · 4 meses.
                    </p>
                    <div className="flex items-center gap-4 text-white/70 text-sm">
                      <span className="flex items-center gap-1"><GraduationCap className="w-4 h-4" /> 12 módulos</span>
                      <span className="flex items-center gap-1"><Users className="w-4 h-4" /> Grupo reducido</span>
                    </div>
                  </div>
                  <Button className="bg-white text-[#8a5a7a] hover:bg-white/90 px-6 py-5 rounded-full self-start lg:self-center">
                    Conocer más <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Link>

            {/* LÍMITES */}
            <Link to="/formaciones/limites-2026" className="block group">
              <div className="relative rounded-2xl overflow-hidden p-8 lg:p-10 hover:shadow-xl transition-all border border-gray-200" 
                   style={{ background: 'linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%)' }}>
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                  <div className="space-y-4">
                    <div className="flex flex-wrap gap-2">
                      <span className="px-3 py-1 bg-gray-200 text-gray-700 rounded-full text-sm">Online en directo · 3 meses</span>
                      <span className="px-3 py-1 bg-[#c9a227] text-white rounded-full text-sm">2ª edición · 2026</span>
                    </div>
                    <h3 className="text-2xl lg:text-3xl font-heading text-[hsl(var(--text-primary))]">
                      LÍMITES · El espacio original de la consciencia
                    </h3>
                    <p className="text-[hsl(var(--text-secondary))] max-w-xl">
                      Un proceso de tres meses para recuperar tu referencia interna, comprender cómo funcionan tus límites y relacionarte sin desaparecer dentro del vínculo. Empezamos el 13 de octubre. Clase 0 disponible ya.
                    </p>
                    <div className="flex flex-wrap items-center gap-4 text-[hsl(var(--text-tertiary))] text-sm">
                      <span className="flex items-center gap-1"><Video className="w-4 h-4" /> Martes · 17:30</span>
                      <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> 14 encuentros</span>
                      <span className="flex items-center gap-1"><Sparkles className="w-4 h-4" /> Clase 0 ya disponible</span>
                    </div>
                  </div>
                  <Button className="bg-[#c9a227] text-white hover:bg-[#b89220] px-6 py-5 rounded-full self-start lg:self-center">
                    Entrar en LÍMITES <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Link>

            {/* CAMPUS */}
            <Link to="/campus-interioridad" className="block group">
              <div className="relative rounded-2xl overflow-hidden bg-[#1e3a5f] p-8 lg:p-10 hover:shadow-xl transition-all">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                  <div className="space-y-4">
                    <div className="flex flex-wrap gap-2">
                      <span className="px-3 py-1 bg-white/20 text-white rounded-full text-sm">Comunidad mensual</span>
                      <span className="px-3 py-1 bg-[#c9a227] text-white rounded-full text-sm">Próximamente</span>
                    </div>
                    <h3 className="text-2xl lg:text-3xl font-heading text-white">
                      Campus de Interioridad
                    </h3>
                    <p className="text-white/80 max-w-xl">
                      Una comunidad de práctica y encuentro. Un espacio para sostener tu proceso con otros.
                    </p>
                    <div className="flex items-center gap-4 text-white/70 text-sm">
                      <span className="flex items-center gap-1"><Users className="w-4 h-4" /> Comunidad activa</span>
                      <span>22€/mes</span>
                    </div>
                  </div>
                  <Button className="bg-white text-[#1e3a5f] hover:bg-white/90 px-6 py-5 rounded-full self-start lg:self-center">
                    Lista de espera <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Link>

            {/* Cursos grabados - link simple */}
            <div className="text-center pt-6">
              <Link to="/formaciones" className="inline-flex items-center gap-2 text-[#c9a227] hover:text-[#b89220] font-medium">
                Ver cursos grabados (a tu ritmo)
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== QUIÉN ACOMPAÑA ==================== */}
      <section className="py-20 lg:py-28 bg-[#faf8f5]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Photo */}
            <div className="flex justify-center">
              <div className="relative">
                <div className="w-72 h-80 lg:w-80 lg:h-96 rounded-2xl overflow-hidden shadow-xl">
                  <img 
                    src="https://res.cloudinary.com/dpppkdhdk/image/upload/v1776874282/yo_landing_inicial_fmuidq.png"
                    alt="Herminio"
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Decorative */}
                <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-[#c9a227]/20 rounded-full -z-10" />
              </div>
            </div>
            
            {/* Content */}
            <div className="space-y-6">
              <div className="w-12 h-[2px] bg-[#c9a227]" />
              <h2 className="text-3xl lg:text-4xl font-heading text-[hsl(var(--text-primary))]">
                Soy Herminio
              </h2>
              <p className="text-lg text-[hsl(var(--text-secondary))] leading-relaxed">
                Acompaño procesos de reconexión interna desde hace más de 15 años. 
                No enseño a escapar de la vida, sino a habitarla con más presencia 
                y menos esfuerzo.
              </p>
              <p className="text-[hsl(var(--text-secondary))] leading-relaxed">
                Mi trabajo no busca darte respuestas, sino ayudarte a encontrar 
                tu propio sostén.
              </p>
              <Link to="/quien-soy" className="inline-flex items-center gap-2 text-[#c9a227] hover:text-[#b89220] font-medium">
                Conoce mi historia completa
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== EBOOK ==================== */}
      <section id="ebook" className="py-20 lg:py-28 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Ebook image */}
            <div className="flex justify-center order-2 lg:order-1">
              <img 
                src="https://res.cloudinary.com/dpppkdhdk/image/upload/v1776874282/ebook_volver_a_habitarte_f1fgs7.png"
                alt="Ebook: Volver a habitarte"
                className="w-56 h-auto rounded-lg shadow-2xl"
              />
            </div>
            
            {/* Form */}
            <div className="space-y-6 order-1 lg:order-2">
              <div className="w-12 h-[2px] bg-[#c9a227]" />
              <h2 className="text-3xl lg:text-4xl font-heading text-[hsl(var(--text-primary))]">
                Un primer paso hacia ti
              </h2>
              <p className="text-lg text-[hsl(var(--text-secondary))]">
                <span className="font-medium text-[hsl(var(--text-primary))]">"Volver a habitarte"</span> — 
                una guía para recuperar tu eje, tu cuerpo y tu claridad interior.
              </p>
              
              <form onSubmit={handleSubscribe} className="space-y-4 pt-4">
                <Input
                  type="email"
                  placeholder="Tu email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="py-6 rounded-xl border-gray-200"
                />
                <div className="flex items-start gap-3">
                  <Checkbox id="consent" checked={consent} onCheckedChange={setConsent} className="mt-1" />
                  <label htmlFor="consent" className="text-sm text-[hsl(var(--text-tertiary))] cursor-pointer leading-relaxed">
                    Acepto recibir el recurso gratuito y comunicaciones relacionadas. He leído la{' '}
                    <Link to="/privacidad" target="_blank" className="text-[#c9a227] hover:underline">Política de privacidad</Link>.
                    Puedes darte de baja en cualquier momento.
                  </label>
                </div>
                <Button type="submit" className="bg-[#c9a227] hover:bg-[#b89220] text-white w-full py-6 rounded-full text-base" disabled={subscribing}>
                  {subscribing ? 'Enviando...' : 'Recibir el ebook gratuito'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== TESTIMONIOS - CARRUSEL ==================== */}
      <section className="py-20 lg:py-28 bg-[#faf8f5]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-heading text-[hsl(var(--text-primary))] mb-4">
              Voces del camino
            </h2>
            <div className="w-16 h-[2px] bg-[#c9a227] mx-auto" />
          </div>
          
          {/* Carousel */}
          <div className="relative px-4 lg:px-12">
            {/* Arrows */}
            <button 
              onClick={prevTestimonial}
              className="absolute left-0 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full border border-[#c9a227]/30 flex items-center justify-center hover:bg-[#c9a227]/10 transition-colors z-10"
              aria-label="Anterior testimonio"
            >
              <ChevronLeft className="w-5 h-5 text-[#c9a227]" />
            </button>
            <button 
              onClick={nextTestimonial}
              className="absolute right-0 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full border border-[#c9a227]/30 flex items-center justify-center hover:bg-[#c9a227]/10 transition-colors z-10"
              aria-label="Siguiente testimonio"
            >
              <ChevronRight className="w-5 h-5 text-[#c9a227]" />
            </button>

            {/* Cards container */}
            <div className="overflow-hidden rounded-2xl">
              <div 
                className="flex transition-transform duration-500"
                style={{ transform: `translateX(-${testimonialIndex * 100}%)` }}
              >
                {testimonials.map((t, i) => (
                  <div key={i} className="w-full flex-shrink-0">
                    <div className="bg-white p-8 lg:p-12 text-center">
                      <div className="w-14 h-14 rounded-full bg-[#c9a227]/10 flex items-center justify-center mx-auto mb-6">
                        <Quote className="w-6 h-6 text-[#c9a227]" />
                      </div>
                      <p className="text-[#c9a227] font-medium mb-4">"{t.highlight}"</p>
                      <p className="text-lg lg:text-xl text-[hsl(var(--text-secondary))] italic leading-relaxed max-w-2xl mx-auto mb-8">
                        "{t.text}"
                      </p>
                      <div className="inline-flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#c9a227]/30 to-[#c9a227]/10" />
                        <div className="text-left">
                          <p className="font-medium text-[hsl(var(--text-primary))]">{t.name}</p>
                          <p className="text-sm text-[#c9a227]">{t.role}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Progress bar */}
            <div className="flex justify-center gap-3 mt-8">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goToTestimonial(i)}
                  className={`h-1 rounded-full transition-all duration-300 ${
                    i === testimonialIndex ? 'bg-[#c9a227] w-8' : 'bg-gray-300 w-4'
                  }`}
                  aria-label={`Ir al testimonio ${i + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ==================== PARA QUIÉN ES ==================== */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-heading text-[hsl(var(--text-primary))] mb-4">
              ¿Es este espacio para ti?
            </h2>
            <div className="w-16 h-[2px] bg-[#c9a227] mx-auto" />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="p-8 rounded-2xl bg-[#faf8f5]">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-[#c9a227]/20 flex items-center justify-center">
                  <Check className="w-5 h-5 text-[#c9a227]" />
                </div>
                <h3 className="text-lg font-heading">Sí, si...</h3>
              </div>
              <ul className="space-y-3">
                {['Sientes que vives hacia afuera', 'Buscas un proceso profundo', 'Valoras el acompañamiento real', 'Quieres aprender a sostenerte'].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-[hsl(var(--text-secondary))]">
                    <Check className="w-4 h-4 text-[#c9a227] mt-1 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="p-8 rounded-2xl bg-gray-50">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                  <X className="w-5 h-5 text-gray-500" />
                </div>
                <h3 className="text-lg font-heading">Quizás no, si...</h3>
              </div>
              <ul className="space-y-3">
                {['Buscas resultados inmediatos', 'Prefieres solo teoría', 'No tienes tiempo para el proceso', 'Esperas respuestas externas'].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-[hsl(var(--text-secondary))]">
                    <X className="w-4 h-4 text-gray-400 mt-1 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== CIERRE ==================== */}
      <section className="py-20 lg:py-28 bg-[#faf8f5]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <blockquote className="font-heading text-3xl lg:text-4xl text-[hsl(var(--text-primary))] leading-relaxed mb-10">
            "No tienes que convertirte en nadie distinto.
            <span className="block mt-2 text-[#c9a227]">Solo volver a casa dentro de ti."</span>
          </blockquote>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/formaciones">
              <Button className="bg-[#c9a227] hover:bg-[#b89220] text-white px-8 py-6 text-base rounded-full">
                Explorar formaciones <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Button onClick={() => scrollToSection('ebook')} variant="outline" className="border-[#c9a227] text-[#c9a227] hover:bg-[#c9a227]/10 px-8 py-6 text-base rounded-full">
                Empezar con el ebook
              </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Landing;
